import * as THREE from 'three';
import type { Heightfield } from './TerrainTypes';
import { heightfieldNormal } from './TerrainSculpt';
import { writeSlopeTint } from './TerrainMaterial';
import { expandRect, isEmptyRect, type SurfaceRect } from './TerrainSurface';

/**
 * Builds a Three.js geometry from a canonical {@link Heightfield}. The grid is centered on the
 * origin, spanning `widthMeters` along X and `lengthMeters` along Z, with height along Y. Geometry
 * is a *view* of the domain heightfield — never the source of truth.
 *
 * Normals come from {@link heightfieldNormal} rather than from the triangles, so that the sculpt
 * tool's partial rebuilds ({@link updateTerrainGeometryRegion}) produce exactly the same shading as
 * a full rebuild. Two paths that disagree by a fraction of a degree would show up as a visible seam
 * at the edge of every stroke.
 */
export function buildTerrainGeometry(field: Heightfield): THREE.BufferGeometry {
  const { cols, rows, widthMeters, lengthMeters, heights } = field;
  const vertexCount = cols * rows;
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const uvs = new Float32Array(vertexCount * 2);

  for (let r = 0; r < rows; r += 1) {
    const v = rows > 1 ? r / (rows - 1) : 0;
    for (let c = 0; c < cols; c += 1) {
      const u = cols > 1 ? c / (cols - 1) : 0;
      const i = r * cols + c;
      positions[i * 3] = (u - 0.5) * widthMeters;
      positions[i * 3 + 1] = heights[i] ?? 0;
      positions[i * 3 + 2] = (v - 0.5) * lengthMeters;
      const n = heightfieldNormal(field, c, r);
      normals[i * 3] = n.x;
      normals[i * 3 + 1] = n.y;
      normals[i * 3 + 2] = n.z;
      uvs[i * 2] = u;
      uvs[i * 2 + 1] = v;
    }
  }

  const indices: number[] = [];
  for (let r = 0; r < rows - 1; r += 1) {
    for (let c = 0; c < cols - 1; c += 1) {
      const a = r * cols + c;
      const b = r * cols + c + 1;
      const d = (r + 1) * cols + c;
      const e = (r + 1) * cols + c + 1;
      indices.push(a, d, b, b, d, e);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * Re-derive positions, normals, and the slope tint for one rectangle of vertices after the sculpt
 * tool has moved heights there.
 *
 * The rectangle is grown by one vertex before normals are computed, because a vertex just outside
 * the edit still has a neighbour inside it and would otherwise keep a normal describing ground that
 * no longer exists — a one-vertex-wide crease around every stroke.
 *
 * Only the touched rows are flagged for upload. Three re-sends whole attributes when
 * `needsUpdate` is set with no update range, which for a 145×145 site is a quarter of a megabyte per
 * dab; a stroke's worth of that is real traffic for no reason.
 */
export function updateTerrainGeometryRegion(
  geometry: THREE.BufferGeometry,
  field: Heightfield,
  rect: SurfaceRect,
): void {
  if (isEmptyRect(rect)) return;
  const position = geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
  const normal = geometry.getAttribute('normal') as THREE.BufferAttribute | undefined;
  const color = geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
  if (!position || !normal) return;

  const positions = position.array as Float32Array;
  const normals = normal.array as Float32Array;
  const colors = color ? (color.array as Float32Array) : null;

  const affected = expandRect(rect, 1, field.cols, field.rows);
  for (let row = affected.y; row < affected.y + affected.height; row += 1) {
    for (let col = affected.x; col < affected.x + affected.width; col += 1) {
      const i = row * field.cols + col;
      positions[i * 3 + 1] = field.heights[i] ?? 0;
      const n = heightfieldNormal(field, col, row);
      normals[i * 3] = n.x;
      normals[i * 3 + 1] = n.y;
      normals[i * 3 + 2] = n.z;
      if (colors) writeSlopeTint(colors, i, n.y);
    }
  }

  // Vertices are row-major, so a rectangle is a contiguous span from its first row to its last.
  const first = affected.y * field.cols + affected.x;
  const last = (affected.y + affected.height - 1) * field.cols + affected.x + affected.width - 1;
  const count = last - first + 1;
  flagRange(position, first * 3, count * 3);
  flagRange(normal, first * 3, count * 3);
  if (color) flagRange(color, first * 3, count * 3);
}

function flagRange(attribute: THREE.BufferAttribute, offset: number, count: number): void {
  // `addUpdateRange` landed in r159; fall back to a whole-attribute upload on anything older.
  const withRanges = attribute as THREE.BufferAttribute & {
    addUpdateRange?: (start: number, count: number) => void;
    clearUpdateRanges?: () => void;
  };
  if (typeof withRanges.addUpdateRange === 'function') {
    withRanges.clearUpdateRanges?.();
    withRanges.addUpdateRange(offset, count);
  }
  attribute.needsUpdate = true;
}
